import React, { useMemo } from 'react';

import { AISmartButtonLegacy, AISmartButtonRefresh } from '@atlassian/ai-smart-button';
import { useSurfaceAIActions } from '@atlassian/ai-smart-button-api';
import { isFedRamp } from '@atlassian/atl-context';

import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';
import { useSessionData } from '@confluence/session-data';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { expValEquals } from '@confluence/feature-experiments';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { useCreateRovoExperienceSidebarController } from '@confluence/conversation-assistant';

import { useCreateObjectSidebarProp } from '../hooks/useCreateObjectSidebarProp';

type AISmartButtonProps = {
	contentId: string;
	contentType: string;
	pageMode: string;
	isDisabled?: boolean;
};

export function AISmartButtonWrapper({
	contentId,
	contentType,
	pageMode,
	isDisabled = false,
}: AISmartButtonProps) {
	const { cloudId, orgId } = useSessionData();
	const isNav4Enabled = useIsNav4Enabled();
	const { setSurfaceMetadata } = useSurfaceAIActions();
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});
	const sidebarApi = useCreateRovoExperienceSidebarController();

	const isEnabled = !isFedRamp() && isAIEnabledForContent && isRovoEnabledForContent;
	const objectSidebar = useCreateObjectSidebarProp();

	// only needed for legacy button
	React.useEffect(() => {
		setSurfaceMetadata({
			siteId: cloudId,
			product: 'confluence',
			contentType,
			pageMode,
		});
	}, [cloudId, setSurfaceMetadata, contentType, pageMode]);

	// Memoize analytics attributes to prevent unnecessary re-renders
	const analyticsAttributes = useMemo(
		() => ({
			pageMode,
		}),
		[pageMode],
	);

	if (!isEnabled) {
		return null;
	}

	return (
		<ErrorBoundary attribution={Attribution.AI_MATE}>
			{expValEquals('rovo_chat_refresh_v1_experiment', 'isRenderRefreshEnabled', true) ? (
				<AISmartButtonRefresh
					product="confluence"
					cloudId={cloudId}
					orgId={orgId}
					isNav4Enabled={isNav4Enabled}
					sidebarApi={sidebarApi}
					productPage={contentType}
					analyticsAttributes={analyticsAttributes}
					isDisabled={isDisabled}
				/>
			) : (
				<AISmartButtonLegacy sidebarApi={objectSidebar} isDisabled={isDisabled} />
			)}
		</ErrorBoundary>
	);
}
